import React, {useEffect, useMemo, useState} from 'react';
import './App.css';
import {Admin, Login, Resource} from 'react-admin';
import {d4Theme} from "./d4theme";
import {Dashboard} from './Dashboard';
import NotFound from "./NotFound";
import {FunctionList} from "./public/function/d4func_list";
import {LocationList} from "./public/location/d4loc_list";
import {ProductList} from "./public/product/d4prod_list";
import {ProductShow} from "./public/product/d4prod_show";
import DeveloperBoardTwoToneIcon from "@mui/icons-material/DeveloperBoardTwoTone";
import LocationCityIcon from '@mui/icons-material/LocationCity';
import FunctionsIcon from '@mui/icons-material/Functions';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import {d4BackgroundImage} from "./Env";
import {OrganisationList} from "./public/organisation/d4org_list";
import {OrganisationShow} from "./public/organisation/d4org_show";
import {LocationShow} from "./public/location/d4loc_show";
import {FunctionShow} from "./public/function/d4func_show";
import polyglotI18nProvider from 'ra-i18n-polyglot';
import german from './i18n/de';
import englishMessages from './i18n/en';
import {BrowserRouter, Route, Routes} from 'react-router-dom';
import {useAuth} from 'react-oidc-context';
import {hasRole, ROLES} from './permissions';


import AuthProvider from './auth/AuthProvider';
import TokenRenewal from './auth/TokenRenewal';
import {createRaAuthProvider} from './auth/raAuthProvider';
import {createDataProvider} from './auth/dataProvider';

const getBrowserLocale = () => {
    const browserLocale = navigator.language || navigator.userLanguage;
    if (browserLocale.startsWith('de')) {
        return 'de';
    } else {
        return 'en';
    }
};


const loadMessages = (locale) => {
    switch (locale) {
        case 'de':
            return german;
        case 'en':
            return englishMessages;
        default:
            return englishMessages;
    }
};

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const MyLoginPage = () => <Login backgroundImage={d4BackgroundImage}/>;
const i18nProvider = polyglotI18nProvider(loadMessages, getBrowserLocale());


// Custom hook to check permissions
const useHasPermission = (authProvider, role) => {
    const [hasPermission, setHasPermission] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (authProvider) {
            authProvider.getPermissions()
                .then(permissions => {
                    setHasPermission(hasRole(permissions, role));
                    setLoading(false);
                })
                .catch(() => {
                    setHasPermission(false);
                    setLoading(false);
                });
        } else {
            setHasPermission(false);
            setLoading(false);
        }
    }, [authProvider, role]);
    return {hasPermission, loading};
};


const AdminApp = () => {
    const auth = useAuth();
    const authProvider = useMemo(() => createRaAuthProvider()(auth), [auth]);
    const dataProvider = useMemo(() => createDataProvider(apiUrl)(auth), [auth]);
    const {
        hasPermission: canAccessNodesRO,
        loading
    } = useHasPermission(authProvider, [ROLES.d5_nodes_ro, ROLES.d5_nodes_rw, ROLES.d5_nodes_priv]);

    return (
        <>
            <TokenRenewal/>
            <Admin
                locale="de"
                i18nProvider={i18nProvider}
                disableTelemetry="true"
                catchAll={NotFound}
                title="d4_"
                theme={d4Theme}
                authProvider={authProvider}
                loginPage={MyLoginPage}
                dashboard={props => <Dashboard {...props} authProvider={authProvider}/>}
                dataProvider={dataProvider}
            >
                {!loading && canAccessNodesRO && (
                    <Resource
                        name="products"
                        list={ProductList}
                        show={ProductShow}
                        icon={DeveloperBoardTwoToneIcon}
                        options={{label: 'resources.products.name'}}
                    />
                )}
                {!loading && canAccessNodesRO && (
                    <Resource
                        name="functions"
                        list={FunctionList}
                        show={FunctionShow}
                        icon={FunctionsIcon}
                        options={{label: 'resources.functions.name'}}
                    />
                )}
                {!loading && canAccessNodesRO && (
                    <Resource
                        name="locations"
                        list={LocationList}
                        show={LocationShow}
                        icon={LocationCityIcon}
                        options={{label: 'resources.locations.name'}}
                    />
                )}
                {!loading && canAccessNodesRO && (
                    <Resource
                        name="organisations"
                        list={OrganisationList}
                        show={OrganisationShow}
                        icon={AccountTreeIcon}
                        options={{label: 'resources.organisations.name'}}
                    />
                )}
                <Resource
                    name="types"
                    options={{label: 'resources.types.name'}}
                />
                <Resource
                    name="scopes"
                    options={{label: 'resources.scopes.name'}}
                />
                <Resource
                    name="aspects"
                    options={{label: 'resources.aspects.name'}}
                />
                <Resource
                    name="tags"
                    options={{label: 'resources.tags.name'}}
                />
            </Admin>
        </>
    );
};

const App = () => (
    <BrowserRouter>
        <AuthProvider>
            <Routes>
                <Route path="/*" element={<AdminApp/>}/>
            </Routes>
        </AuthProvider>
    </BrowserRouter>
);

export default App;
